/**
 * ReasonPickerDialog - shared confirmation dialog for approve / deny /
 * cancel / waive-fee flows.
 *
 * Reason lists are the tenant's own ``TransferReason`` rows, passed in by
 * the caller. They were a hardcoded frontend constant named after one
 * tenant, so every other tenant saw that tenant's vocabulary and a product
 * owner could not add or rename a reason without a frontend release.
 */

import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'

export interface ReasonOption {
  code: string
  label: string
}

interface ReasonPickerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  /** When empty/undefined, the dialog is a plain confirm. */
  options?: ReasonOption[]
  /**
   * The options are still being fetched.
   *
   * Without this an in-flight fetch looks exactly like "this dialog has no
   * reasons": `options` is empty, the picker is skipped, and a decision that
   * requires a reason is recorded with none. Now the picker shows that it is
   * loading and confirm waits for it.
   */
  optionsLoading?: boolean
  /** When true, the user must pick a reason before confirming. */
  reasonRequired?: boolean
  /** Free-text field (for e.g. fee-waiver notes). */
  requireNote?: boolean
  noteLabel?: string
  noteMinLength?: number
  confirmLabel: string
  confirmVariant?: 'default' | 'destructive'
  pending?: boolean
  onConfirm: (result: { reasonCode?: string; note?: string }) => void
}

export function ReasonPickerDialog({
  open,
  onOpenChange,
  title,
  description,
  options,
  optionsLoading = false,
  reasonRequired = false,
  requireNote = false,
  noteLabel = 'Notes',
  noteMinLength = 0,
  confirmLabel,
  confirmVariant = 'default',
  pending = false,
  onConfirm,
}: ReasonPickerDialogProps) {
  const [reasonCode, setReasonCode] = useState<string>('')
  const [note, setNote] = useState<string>('')

  const hasOptions = !!options && options.length > 0
  // Render the picker while the fetch is in flight too, so an in-flight list
  // never silently degrades a reason-required dialog into a plain confirm.
  const showOptions = hasOptions || optionsLoading
  // Loading is in-flight async state, the one case where blocking confirm is
  // right: the user cannot supply what has not arrived. A list that has
  // loaded EMPTY is different - the tenant has configured no reasons, which
  // the user cannot fix from here, so confirm stays available.
  const reasonOk =
    !reasonRequired || (!optionsLoading && (!hasOptions || !!reasonCode))
  const noteOk = !requireNote || note.trim().length >= noteMinLength
  const canConfirm = reasonOk && noteOk && !pending

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setReasonCode('')
      setNote('')
    }
    onOpenChange(next)
  }

  const handleConfirm = () => {
    if (!canConfirm) return
    onConfirm({
      reasonCode: hasOptions ? reasonCode || undefined : undefined,
      note: requireNote ? note.trim() : undefined,
    })
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          {showOptions && (
            <div>
              <Label className="clara-section-label mb-1.5 block">
                Reason {reasonRequired && <span className="text-[var(--status-error)]">*</span>}
              </Label>
              {optionsLoading ? (
                <p
                  className="m-0 text-[12px] font-light text-[var(--ink-3)]"
                  data-testid="reason-options-loading"
                >
                  Loading reasons...
                </p>
              ) : (
                <Select value={reasonCode} onValueChange={setReasonCode}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {options!.map((o) => (
                      <SelectItem key={o.code} value={o.code}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {requireNote && (
            <div>
              <Label className="clara-section-label mb-1.5 block">
                {noteLabel} {noteMinLength > 0 && <span className="text-[var(--status-error)]">*</span>}
              </Label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={4}
                className="w-full resize-y rounded-[6px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2 text-[13px] text-[var(--ink)] placeholder:text-[var(--ink-3)] focus:border-[var(--ink-3)] focus:outline-none"
                placeholder={
                  noteMinLength > 0
                    ? `${noteMinLength}+ characters required`
                    : 'Optional'
                }
                data-testid="reason-note"
              />
              {noteMinLength > 0 && (
                <p className="mt-1 text-[11px] text-[var(--ink-3)]">
                  {note.trim().length} / {noteMinLength} minimum characters
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant={confirmVariant}
            onClick={handleConfirm}
            disabled={!canConfirm}
            data-testid="reason-confirm"
          >
            {pending ? 'Working\u2026' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

